import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft, Loader2, TrendingUp, Users, CalendarDays, DollarSign,
  BarChart3, PieChart as PieChartIcon, Briefcase
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { AdminSubNav, SUBNAV_ANALYTICS } from '../../components/admin/AdminSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '12m', label: '12 Months' },
];

const PIE_COLORS = ['#F56F05', '#10B981', '#3B82F6', '#8B5CF6', '#EF4444', '#F59E0B'];

const JOB_TYPE_OPTIONS = [
  'Initial Clean', 'Recurring Visit', 'Walkthrough', 'Move-In', 'Move-Out', 'Touch-Up',
];

const STATUS_COLORS = {
  Booked: '#3B82F6',
  Confirmed: '#8B5CF6',
  'In Progress': '#F59E0B',
  Completed: '#10B981',
  Canceled: '#EF4444',
};

const AdminAnalyticsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [jobTypeFilter, setJobTypeFilter] = useState('');  // '' = all

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ period });
      if (jobTypeFilter) qs.set('job_type', jobTypeFilter);
      const res = await axios.get(`${API_URL}/api/admin/analytics?${qs}`, { headers });
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, [headers, period, jobTypeFilter]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  const { summary, revenue_over_time, top_services, status_breakdown, client_acquisition, job_type_breakdown = [] } = data;

  return (
    <div className="min-h-screen" data-testid="admin-analytics-page">
      <header className="gg-legacy-head">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center min-h-16 gap-4 py-3 flex-wrap">
          <Button variant="ghost" size="icon" onClick={back} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#77776e]">Revenue, visits and clients for the selected period · Pacific time</p>
          </div>
          <div className="w-full sm:w-auto overflow-x-auto">
            <Tabs value={period} onValueChange={setPeriod}>
              <TabsList>
                {PERIOD_OPTIONS.map(o => (
                  <TabsTrigger key={o.value} value={o.value} data-testid={`period-${o.value}`}>{o.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </header>
      <AdminSubNav section="Analytics" items={SUBNAV_ANALYTICS} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Job Type filter chips */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="job-type-filter-row">
          <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Job Type</span>
          <button
            onClick={() => setJobTypeFilter('')}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${jobTypeFilter === '' ? 'bg-[#252a20] text-white border-[#252a20]' : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300'}`}
            data-testid="jt-filter-all"
          >
            All
          </button>
          {JOB_TYPE_OPTIONS.map(jt => (
            <button
              key={jt}
              onClick={() => setJobTypeFilter(jt)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${jobTypeFilter === jt ? 'bg-[#252a20] text-white border-[#252a20]' : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300'}`}
              data-testid={`jt-filter-${jt.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              {jt}
            </button>
          ))}
        </div>
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 rounded-[9px] border border-[#e7e4db] bg-[#faf9f6] overflow-hidden" data-testid="analytics-kpis">
          {[
            ['REVENUE', `$${summary.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, `Avg ticket $${summary.avg_ticket}`, 'kpi-revenue', '/admin/billing'],
            ['APPOINTMENTS', summary.total_appointments, `${summary.completed_appointments} completed`, 'kpi-appointments', '/admin/appointments'],
            ['CLIENTS', summary.total_clients, `${summary.new_clients} new this period`, 'kpi-clients', '/admin/customers'],
            ['TEAM PAYOUTS', `$${summary.total_payouts.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, `${summary.payout_count} processed`, 'kpi-payouts', '/admin/payouts'],
          ].map(([l, v, n, t, to], k) => (
            <motion.button key={t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: k * 0.05 }} onClick={() => navigate(to)}
              className="text-left p-5 sm:p-6 border-r border-b md:border-b-0 border-[#e7e4db] last:border-r-0 hover:bg-[#eeeae0] transition-colors" data-testid={`${t}-card`}>
              <div className="flex justify-between text-[9px] tracking-[0.1em] font-bold text-[#74776a]"><span>{l}</span><span>↗</span></div>
              <strong className="block text-3xl sm:text-4xl font-medium tracking-tight text-[#20211d] my-1.5" data-testid={t}>{v}</strong>
              <small className="text-[11px] text-[#77776e]">{n}</small>
            </motion.button>
          ))}
        </div>

        {/* Charts Row 1: Revenue + Client Acquisition */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-2">
            <Card className="h-full rounded-[10px] border border-[#e7e4db] bg-[#fffefa] shadow-none">
              <CardHeader>
                <CardTitle className="text-base font-heading flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#F56F05]" />Revenue Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                {revenue_over_time.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={revenue_over_time}>
                      <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#F56F05" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#F56F05" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#a1a1aa' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#a1a1aa' }} tickFormatter={v => `$${v}`} />
                      <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7' }} />
                      <Area type="monotone" dataKey="revenue" stroke="#F56F05" strokeWidth={2} fill="url(#revGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] flex items-center justify-center text-zinc-400">
                    <p>No revenue data for this period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card className="h-full rounded-[10px] border border-[#e7e4db] bg-[#fffefa] shadow-none">
              <CardHeader>
                <CardTitle className="text-base font-heading flex items-center gap-2"><PieChartIcon className="h-4 w-4 text-blue-500" />Appointment Status</CardTitle>
              </CardHeader>
              <CardContent>
                {status_breakdown.length > 0 ? (
                  <div>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={status_breakdown}
                          cx="50%" cy="50%"
                          innerRadius={50} outerRadius={75}
                          dataKey="count"
                          nameKey="status"
                          stroke="none"
                        >
                          {status_breakdown.map((entry, i) => (
                            <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-2 justify-center mt-2">
                      {status_breakdown.map((entry, i) => (
                        <div key={entry.status} className="flex items-center gap-1.5 text-xs">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span className="text-zinc-600">{entry.status} ({entry.count})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="h-[180px] flex items-center justify-center text-zinc-400">
                    <p>No data</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Charts Row 2: Top Services + Client Acquisition */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="rounded-[10px] border border-[#e7e4db] bg-[#fffefa] shadow-none">
              <CardHeader>
                <CardTitle className="text-base font-heading flex items-center gap-2"><Briefcase className="h-4 w-4 text-purple-500" />Top Services</CardTitle>
              </CardHeader>
              <CardContent>
                {top_services.length > 0 ? (
                  <div>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={top_services} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 12, fill: '#a1a1aa' }} tickFormatter={v => `$${v}`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} width={90} interval={0} />
                        <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7' }} />
                        <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                          {top_services.map((svc, i) => (
                            <Cell key={svc.id || svc.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="mt-3 space-y-1.5">
                      {top_services.map((s, i) => (
                        <div key={s.id} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                            <span className="text-zinc-700">{s.name}</span>
                          </div>
                          <span className="text-zinc-500">{s.count} jobs · ${s.revenue.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-zinc-400">
                    <p>No service data for this period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <Card className="rounded-[10px] border border-[#e7e4db] bg-[#fffefa] shadow-none">
              <CardHeader>
                <CardTitle className="text-base font-heading flex items-center gap-2"><Users className="h-4 w-4 text-green-500" />New Clients</CardTitle>
              </CardHeader>
              <CardContent>
                {client_acquisition.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={client_acquisition}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#a1a1aa' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#a1a1aa' }} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7' }} />
                      <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} name="New Clients" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] flex items-center justify-center text-zinc-400">
                    <p>No new client data for this period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Job Type Breakdown — always shows all types regardless of filter, so admin can see the split at a glance */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="border-0 shadow-sm" data-testid="job-type-breakdown-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-blue-500" /> Job Type Breakdown
              </CardTitle>
              <span className="text-xs text-zinc-400">All job types · click a chip above to filter the charts</span>
            </CardHeader>
            <CardContent>
              {job_type_breakdown.length === 0 ? (
                <div className="py-10 text-center text-zinc-400 text-sm">No job type data for this period yet.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {job_type_breakdown.map((row, i) => {
                    const isActive = jobTypeFilter === row.job_type;
                    const clickable = row.job_type !== '(Unset)';
                    return (
                      <button
                        key={row.job_type}
                        onClick={() => clickable && setJobTypeFilter(isActive ? '' : row.job_type)}
                        disabled={!clickable}
                        className={`text-left p-3 rounded-xl border transition-all ${isActive
                          ? 'bg-[#F56F05]/10 border-[#F56F05] shadow-sm'
                          : clickable
                            ? 'bg-white border-zinc-200 hover:border-zinc-300 hover:shadow-sm'
                            : 'bg-zinc-50 border-zinc-100 cursor-default'}`}
                        data-testid={`jt-breakdown-${row.job_type.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <p className="text-[11px] uppercase font-semibold text-zinc-500 truncate">{row.job_type}</p>
                        </div>
                        <p className="text-xl font-bold text-zinc-900">{row.count}</p>
                        <p className="text-[11px] text-zinc-500">
                          {row.revenue > 0 ? `$${row.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'no revenue'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default AdminAnalyticsPage;
